import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { analyzeCardScan, ScanApiError } from "../lib/api";
import { canonicalScanSide } from "../lib/card-scan-sides";
import { SparklesIcon } from "./SparklesIcon";

type Side = "left" | "right";
type Stage = "prepare" | "preview" | "analyzing" | "feedback";

interface CardScanOverlayProps {
  gameId: string;
  handId?: string;
  trump: string;
  swapped: boolean;
  revision: React.MutableRefObject<number>;
  onApply: (left: number, right: number, revision: number) => boolean;
  onClose: () => void;
}

const MAX_FILE_BYTES = 20 * 1024 * 1024;
const MAX_JPEG_BYTES = 512 * 1024;
let pageCooldown = { until: 0, reason: "rate_limited" };

function messageFor(reason: string): string {
  switch (reason) {
    case "daily_quota":
      return "Le scanner a atteint sa limite aujourd'hui. Saisissez les points manuellement.";
    case "rate_limited":
      return "Trop de scans rapprochés. Vous pouvez saisir les points maintenant.";
    case "zero_points_with_tricks":
      return "Ce tas contient des plis mais vaut zéro point. Le calcul actuel assimile 0 point à un capot : vérifiez le décompte avant d'enregistrer.";
    case "count_mismatch":
      return "Le scanner n'a pas reconnu un multiple de 4 cartes. Étalez-les et réessayez.";
    case "duplicate_card":
    case "read_disagreement":
    case "unreadable_card":
    case "poor_image":
    case "model_output_invalid":
      return "Certaines cartes ne sont pas assez lisibles. Réarrangez-les et réessayez.";
    case "too_many_cards":
      return "Ce tas contient plus de 16 cartes. Photographiez l'autre équipe.";
    case "no_cards":
      return "Aucune carte lisible : réessayez ou saisissez les points.";
    case "timeout":
      return "Le scan prend trop de temps. Saisissez les points ou réessayez plus tard.";
    default:
      return "Scanner indisponible. Vous pouvez saisir les points manuellement.";
  }
}

function asDataUri(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      typeof reader.result === "string"
        ? resolve(reader.result)
        : reject(new Error("JPEG"));
    reader.onerror = () => reject(new Error("JPEG"));
    reader.readAsDataURL(blob);
  });
}

async function prepareImage(file: File): Promise<{ uri: string; url: string }> {
  if (file.size > MAX_FILE_BYTES) throw new Error("La photo dépasse 20 Mo.");
  let bitmap: ImageBitmap | null = null;
  try {
    // Browser decode handles EXIF orientation; drawing the decoded bitmap does not rotate it again.
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    throw new Error("Cette image ne peut pas être lue. Reprenez-la en JPEG.");
  }
  try {
    const { width, height } = bitmap;
    if (width < 400 || height < 400 || width * height > 24_000_000) {
      throw new Error("La photo est trop petite ou trop grande.");
    }
    const scale = Math.min(1, 2048 / Math.max(width, height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Impossible de préparer cette photo.");
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    for (const quality of [0.9, 0.78, 0.65]) {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", quality),
      );
      if (blob && blob.size <= MAX_JPEG_BYTES) {
        return { uri: await asDataUri(blob), url: URL.createObjectURL(blob) };
      }
    }
    throw new Error(
      "Les cartes sont trop petites pour être lues dans une seule photo. Rapprochez l'appareil ou saisissez les points.",
    );
  } finally {
    bitmap.close();
  }
}

export function CardScanOverlay({
  gameId,
  handId,
  trump,
  swapped,
  revision,
  onApply,
  onClose,
}: CardScanOverlayProps) {
  const titleId = useId();
  const fileRef = useRef<HTMLInputElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const sequence = useRef(0);
  const closeHandler = useRef(onClose);
  closeHandler.current = onClose;
  const [stage, setStage] = useState<Stage>("prepare");
  const [side, setSide] = useState<Side | null>(null);
  const [lastTrick, setLastTrick] = useState<Side | null>(null);
  const [photo, setPhoto] = useState<{ uri: string; url: string } | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [serviceFailure, setServiceFailure] = useState(false);
  const [cooldown, setCooldown] = useState(() => pageCooldown.until);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const originalOverflow = document.body.style.overflow;
    const root = document.getElementById("root");
    const wasInert = root?.inert ?? false;
    if (root) root.inert = true;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const onKeys = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeHandler.current();
      }
      if (event.key !== "Tab") return;
      const dialog = closeRef.current?.closest('[role="dialog"]');
      const focusable = Array.from(
        dialog?.querySelectorAll<HTMLElement>(
          'button:not(:disabled), input:not(:disabled), [href], [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((item) => item.getClientRects().length > 0);
      if (!focusable.length) return;
      const first = focusable[0],
        last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    const marker = crypto.randomUUID();
    history.pushState({ ...history.state, coincheCardScan: marker }, "");
    const onBack = () => closeHandler.current();
    window.addEventListener("popstate", onBack);
    document.addEventListener("keydown", onKeys);
    return () => {
      controllerRef.current?.abort();
      sequence.current++;
      document.removeEventListener("keydown", onKeys);
      window.removeEventListener("popstate", onBack);
      if (history.state?.coincheCardScan === marker) history.back();
      if (root) root.inert = wasInert;
      document.body.style.overflow = originalOverflow;
      previous?.focus();
    };
  }, []);

  useEffect(
    () => () => {
      if (photo) URL.revokeObjectURL(photo.url);
    },
    [photo],
  );

  useEffect(() => {
    if (cooldown <= Date.now()) return;
    const timer = window.setTimeout(
      () => setCooldown(0),
      cooldown - Date.now(),
    );
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  function resetPhoto() {
    controllerRef.current?.abort();
    sequence.current++;
    setPhoto(null);
    setMessage(null);
    setServiceFailure(false);
    setStage("prepare");
  }

  async function choosePhoto(file?: File) {
    if (!file) return;
    setMessage(null);
    const ticket = ++sequence.current;
    try {
      const prepared = await prepareImage(file);
      if (ticket !== sequence.current) {
        URL.revokeObjectURL(prepared.url);
        return;
      }
      setPhoto(prepared);
      setStage("preview");
    } catch (error) {
      if (ticket === sequence.current)
        setMessage(
          error instanceof Error ? error.message : messageFor("unavailable"),
        );
    }
  }

  async function analyze() {
    if (!photo || !side || !lastTrick) return;
    if (Date.now() < cooldown) {
      setMessage(messageFor(pageCooldown.reason));
      return;
    }
    const controller = new AbortController();
    controllerRef.current = controller;
    const ticket = ++sequence.current;
    const capturedRevision = revision.current;
    const input = {
      requestId: crypto.randomUUID(),
      handId: handId ?? null,
      side: canonicalScanSide(side, swapped),
      lastTrickSide: canonicalScanSide(lastTrick, swapped),
      trump,
      image: photo.uri,
    };
    let timedOut = false;
    const timeout = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 35_000);
    setStage("analyzing");
    setMessage(null);
    setServiceFailure(false);
    try {
      const result = await analyzeCardScan(gameId, input, controller.signal);
      if (ticket !== sequence.current || controller.signal.aborted) return;
      if (result.status === "accepted") {
        if (onApply(result.pointsLeft, result.pointsRight, capturedRevision)) {
          onClose();
          return;
        }
        setMessage(
          "La main a changé pendant le scan. Reprenez une photo si nécessaire.",
        );
      } else {
        setMessage(messageFor(result.reason));
      }
    } catch (error) {
      if (
        ticket !== sequence.current ||
        (controller.signal.aborted && !timedOut)
      )
        return;
      if (error instanceof ScanApiError) {
        setServiceFailure(true);
        if (error.retryAt) {
          const value = Date.parse(error.retryAt);
          if (Number.isFinite(value)) {
            pageCooldown = { until: value, reason: error.reason };
            setCooldown(value);
          }
        }
        setMessage(messageFor(error.reason));
      } else {
        setServiceFailure(true);
        setMessage(messageFor(timedOut ? "timeout" : "unavailable"));
      }
    } finally {
      window.clearTimeout(timeout);
      if (ticket === sequence.current) {
        controllerRef.current = null;
        setStage("feedback");
      }
    }
  }

  const choices = (
    value: Side | null,
    set: (side: Side) => void,
    label: string,
  ) => (
    <fieldset className="space-y-2">
      <legend className="font-semibold text-ink">{label}</legend>
      <div className="grid grid-cols-2 gap-3">
        {(["left", "right"] as const).map((choice) => (
          <button
            key={choice}
            type="button"
            aria-pressed={value === choice}
            className={
              value === choice
                ? "btn-secondary !border-ember !text-ember"
                : "btn-secondary"
            }
            onClick={() => set(choice)}
            disabled={stage === "analyzing"}
          >
            {choice === "left" ? "Nous" : "Eux"}
          </button>
        ))}
      </div>
    </fieldset>
  );

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-[110] overflow-y-auto bg-white"
    >
      <header className="sticky top-0 z-10 border-b bg-white/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-lg items-center justify-between gap-3">
          <h1
            id={titleId}
            className="flex items-center gap-2 text-xl font-bold text-ink"
          >
            <SparklesIcon className="h-6 w-6 text-violet-500" />
            Scanner les cartes
          </h1>
          <button
            ref={closeRef}
            type="button"
            aria-label="Fermer le scanner"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-ink transition hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember"
            onClick={onClose}
          >
            <svg
              viewBox="0 0 24 24"
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-lg space-y-5 px-4 py-5">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="sr-only"
          tabIndex={-1}
          onChange={(event) => {
            void choosePhoto(event.target.files?.[0]);
          }}
        />
        {(stage === "prepare" || stage === "feedback") && (
          <section className="card space-y-4">
            <h2 className="text-lg font-semibold">Préparer la photo</h2>
            <p>
              Choisissez le tas avec le moins de cartes : Nous ou Eux. En cas
              d'égalité, choisissez l'un des deux.
            </p>
            {choices(side, setSide, "À qui est le tas ?")}
            <p className="text-sm text-smoke">
              Aucun pli ? Saisissez les points manuellement.
            </p>
            {choices(lastTrick, setLastTrick, "Qui a gagné le dernier pli ?")}
            <p>
              Étalez toutes les cartes de ce tas, faces visibles. Elles peuvent
              se chevaucher si le coin de chaque carte (valeur et symbole) reste
              visible. Utilisez un fond uni, évitez les reflets et gardez toutes
              les cartes dans le cadre.
            </p>
            {Date.now() < cooldown && (
              <p role="status">{messageFor(pageCooldown.reason)}</p>
            )}
            {stage === "prepare" && (
              <button
                type="button"
                className="btn-primary w-full"
                disabled={!side || !lastTrick || Date.now() < cooldown}
                onClick={() => {
                  if (fileRef.current) {
                    fileRef.current.value = "";
                    fileRef.current.click();
                  }
                }}
              >
                Prendre la photo
              </button>
            )}
          </section>
        )}
        {(stage === "preview" ||
          stage === "analyzing" ||
          stage === "feedback") &&
          photo && (
            <section className="card overflow-hidden !p-0">
              <div className="relative">
                <img
                  src={photo.url}
                  alt="Toutes les cartes à analyser"
                  className="block w-full"
                />
                {stage === "analyzing" && (
                  <div
                    data-testid="scan-processing"
                    className="scan-processing"
                  >
                    <p role="status" aria-live="polite" className="sr-only">
                      Analyse en cours…
                    </p>
                  </div>
                )}
              </div>
              {stage !== "analyzing" && (
                <div className="space-y-3 p-4">
                  {stage === "preview" && (
                    <button
                      type="button"
                      className="btn-ai w-full"
                      onClick={() => void analyze()}
                    >
                      <SparklesIcon />
                      Analyser les cartes
                    </button>
                  )}
                  {stage === "feedback" && (
                    <>
                      {serviceFailure && Date.now() >= cooldown && (
                        <button
                          type="button"
                          className="btn-secondary w-full"
                          onClick={() => void analyze()}
                        >
                          Réessayer l'analyse
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn-secondary w-full"
                        onClick={resetPhoto}
                      >
                        Réarranger et reprendre la photo
                      </button>
                    </>
                  )}
                  {stage === "preview" && (
                    <button
                      type="button"
                      className="btn-secondary w-full"
                      onClick={resetPhoto}
                    >
                      Reprendre la photo ou revoir les conseils
                    </button>
                  )}
                </div>
              )}
            </section>
          )}
        {message && (
          <p
            role="alert"
            className="rounded-lg border border-amber-300 bg-amber-50 p-3"
          >
            {message}
          </p>
        )}
        <button
          type="button"
          className="btn-secondary w-full"
          onClick={onClose}
        >
          Saisir les points manuellement
        </button>
      </main>
    </div>,
    document.body,
  );
}
